import type { RevalidationListParams, RiskFinancingRevalidations } from './revalidationList.types'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { queryKeys } from '~/queryClient'
import financialBff from '~/services-v2/financial-bff/financial-bff'
import { REVALIDATIONS_QUERY_STALE_MS } from './revalidationQuery.constants'

const PAGE_SIZE = 20

export type RevalidationListBaseParams = Omit<RevalidationListParams, 'page'>

function buildSort(p: RevalidationListBaseParams): string | undefined {
  if (!p.order_by || !p.order_dir)
    return undefined
  const sortField = p.order_by === 'status' ? 'revalidation_status' : p.order_by
  return `${sortField},${p.order_dir}`
}

export default function useRevalidationList(params: Ref<RevalidationListBaseParams>) {
  return useInfiniteQuery({
    queryKey: computed(() => [...queryKeys.REVALIDATIONS.LIST, params.value]),
    queryFn: async ({ queryKey, pageParam }): Promise<RiskFinancingRevalidations> => {
      const p = queryKey[queryKey.length - 1] as RevalidationListBaseParams
      const page = pageParam as number

      const res = await financialBff('query')({
        risk_financing_revalidations: [
          {
            input: {
              ...(p.brand != null && p.brand !== '' ? { brand: p.brand } : {}),
              ...(p.status
                ? { status: Array.isArray(p.status) ? p.status : [p.status] }
                : {}),
              sort: buildSort(p),
              page,
              page_size: PAGE_SIZE,
            },
          },
          {
            items: {
              status: true,
              project_name: true,
              plant_name: true,
              reference_inverter_brand: true,
              revalidation_id: true,
              expected_total_inverters: true,
              latest_submission: {
                attempt_status: true,
                serial_number: true,
              },
              latest_attempts: {
                attempt_status: true,
                serial_number: true,
                brand: true,
                submitted_at: true,
                processed_at: true,
                reason_code: true,
              },
            },
            pagination: {
              page: true,
              page_size: true,
              total_items: true,
              total_pages: true,
            },
          },
        ],
      })

      const list = res.risk_financing_revalidations
      if (!list)
        throw new Error('Listagem de revalidações: resposta vazia do BFF.')

      return list as RiskFinancingRevalidations
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.pagination
      if (!pagination)
        return undefined
      const { page, total_pages } = pagination
      if (page == null || total_pages == null)
        return undefined
      return page < total_pages ? page + 1 : undefined
    },
    staleTime: REVALIDATIONS_QUERY_STALE_MS,
  })
}
